import { useEffect, useRef, useState } from 'react';
import { api, getToken } from '../utils/helpers';

// Frames and UTF-8 characters may span network chunks. A stream without DONE is incomplete.
export async function readAnalysisStream(body, onText) {
  if (!body?.getReader) throw Object.assign(new Error(), { code: 'ANALYSIS_STREAM_INTERRUPTED' });
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '', text = '', finished = false;
  const consume = line => {
    if (!line.startsWith('data:')) return;
    const data = line.slice(5).trim();
    if (data === '[DONE]') { finished = true; return; }
    if (!data) return;
    const event = JSON.parse(data);
    if (event.error) throw Object.assign(new Error(), { code: event.code || 'ANALYSIS_CHAT_FAILED' });
    if (typeof event.text === 'string') { text += event.text; onText(text); }
  };
  try {
    while (!finished) {
      const { value, done } = await reader.read();
      buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });
      let newline;
      while (!finished && (newline = buffer.indexOf('\n')) >= 0) {
        consume(buffer.slice(0, newline).replace(/\r$/, ''));
        buffer = buffer.slice(newline + 1);
      }
      if (done) { if (buffer && !finished) consume(buffer.replace(/\r$/, '')); break; }
    }
    if (!finished || !text.trim()) throw Object.assign(new Error(), { code: 'ANALYSIS_STREAM_INTERRUPTED' });
    return text;
  } finally { await reader.cancel().catch(() => {}); reader.releaseLock(); }
}

export default function useErrorAnalysis(instanceId, enabled = true) {
  const [store, setStore] = useState({ scope: instanceId, entries: {} });
  const scope = useRef(instanceId);
  const entries = useRef({});
  const pending = useRef(new Map());
  if (scope.current !== instanceId) { scope.current = instanceId; entries.current = {}; }
  useEffect(() => {
    const requests = pending.current;
    return () => { requests.forEach(request => request.controller.abort()); requests.clear(); };
  }, [instanceId]);
  const update = (guid, patch) => {
    entries.current = { ...entries.current, [guid]: { ...entries.current[guid], ...patch } };
    setStore({ scope: instanceId, entries: entries.current });
  };
  const begin = guid => {
    pending.current.get(guid)?.controller.abort();
    const request = { controller: new AbortController(), instanceId };
    pending.current.set(guid, request);
    return { request, current: () => scope.current === instanceId && pending.current.get(guid) === request && !request.controller.signal.aborted };
  };
  const analyze = async (guid, flowName, errorMessage, analysisMode = 'simple', sprache = 'de') => {
    if (!enabled) return;
    const { request, current } = begin(guid);
    update(guid, { loading: true, chatting: false, error: null, chatError: null, stopped: false,
      analysis: '', messages: [], streamText: '', contextToken: null, retryMessages: null, analysisMode, sprache,
      source: { flowName, errorMessage }, stats: null, errorStep: null, availability: null });
    const timeout = setTimeout(() => { if (current()) { update(guid, { loading: false, error: 'ANALYSIS_TIMEOUT' }); request.controller.abort(); } }, 180000);
    try {
      const result = await api(`/api/${encodeURIComponent(instanceId)}/ai-error-analysis`, {
        method: 'POST', signal: request.controller.signal, timeout: 180000,
        body: JSON.stringify({ errorMessage, flowName, flowId: flowName, messageGuid: guid, analysisMode, sprache }),
      });
      if (!current()) return;
      if (!result.analysis?.trim()) throw Object.assign(new Error(), { code: 'ANALYSIS_EMPTY' });
      update(guid, { ...result, loading: false, messages: [], analysisMode });
    } catch (error) { if (current()) update(guid, { loading: false, error: error.code || 'ANALYSIS_FAILED' }); }
    finally { clearTimeout(timeout); if (pending.current.get(guid) === request) pending.current.delete(guid); }
  };
  const send = async (guid, input, retry = false) => {
    const entry = entries.current[guid];
    if (!enabled || !entry?.contextToken || entry.chatting || entry.loading || (!retry && !input?.trim())) return;
    const messages = retry ? entry.retryMessages : [...entry.messages, { role: 'user', content: input.trim() }];
    if (!messages?.length) return;
    const { request, current } = begin(guid);
    update(guid, { messages, chatting: true, chatError: null, stopped: false, streamText: '', retryMessages: messages });
    const timeout = setTimeout(() => { if (current()) { update(guid, { chatting: false, chatError: 'ANALYSIS_TIMEOUT' }); request.controller.abort(); } }, 180000);
    try {
      const response = await fetch(`/api/${encodeURIComponent(instanceId)}/ai-error-analysis/chat`, {
        method: 'POST', signal: request.controller.signal,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}`, 'Accept-Language': entry.sprache },
        body: JSON.stringify({ contextToken: entry.contextToken, messages, sprache: entry.sprache }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw Object.assign(new Error(), { code: response.status === 410 ? 'ANALYSIS_CONTEXT_EXPIRED' : body.code || 'ANALYSIS_CHAT_FAILED' });
      }
      const content = await readAnalysisStream(response.body, streamText => { if (current()) update(guid, { streamText }); });
      if (current()) update(guid, { messages: [...messages, { role: 'assistant', content }], chatting: false, streamText: '', retryMessages: null });
    } catch (error) { if (current()) update(guid, { chatting: false, chatError: error.code || 'ANALYSIS_CHAT_FAILED' }); }
    finally { clearTimeout(timeout); if (pending.current.get(guid) === request) pending.current.delete(guid); }
  };
  const stop = guid => {
    pending.current.get(guid)?.controller.abort(); pending.current.delete(guid);
    update(guid, { loading: false, chatting: false, stopped: true });
  };
  return { entries: store.scope === instanceId ? store.entries : {}, analyze, send, stop };
}
